// pages/404.js

"use client"

import Link from "next/link"
import { usePathname, useSearchParams } from "next/navigation"
import { useEffect, Suspense } from "react"

import { createReport404Data, type Report404Data } from "@/types/404-tracking"

function NotFoundTracker() {
  /**
   * PSEUDO CODE: 404 Page Tracking Flow
   *
   * 1. Get current pathname and search params from Next.js router
   * 2. On component mount → send tracking data to /api/track-404
   * 3. Classify request type (page vs static asset)
   * 4. Include all metadata for analysis
   * 5. Display user-friendly 404 page
   *
   * WHY: Captures 404s that reach the React application layer
   */

  const pathname = usePathname()
  const searchParams = useSearchParams()

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log(
      "process.env.NEXT_PUBLIC_VERCEL_ENV",
      process.env.NEXT_PUBLIC_VERCEL_ENV,
    )
    // Only track 404s in production
    if (process.env.NEXT_PUBLIC_VERCEL_ENV !== "production") {
      return
    }

    // TRACKING LOGIC: Send 404 data to tracking API
    const track404 = async () => {
      try {
        // STEP 1: Build full path with query parameters
        const fullPath =
          pathname +
          (searchParams.toString() ? `?${searchParams.toString()}` : "")

        // STEP 2: Create type-safe payload
        const payload: Report404Data = createReport404Data({
          path: fullPath,
          pathname,
          search: searchParams.toString(),
          type: "404",
          // CLASSIFICATION: Determine if this is a static asset or page
          isStaticAsset:
            /\.(js|css|woff|woff2|ttf|otf|eot|svg|png|jpg|jpeg|gif|webp|ico|pdf|zip|mp4|mp3)$/i.test(
              pathname,
            ),
          isPage: !pathname.includes("."),
          referrer: document.referrer || "direct",
          userAgent: navigator.userAgent,
        })

        // STEP 3: Send tracking data
        await fetch("/api/track-404", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        })
      } catch (error) {
        // FAIL SAFE: Don't break the 404 page if tracking fails

        console.error("Failed to track 404:", error)
      }
    }

    track404()
  }, [pathname, searchParams])

  return null
}

export default function NotFound() {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen text-gray-300">
      <h1>404 - Page Not Found</h1>
      <p className="my-4">The page you are looking for does not exist.</p>
      <Link href="/" className="text-blue-50 hover:text-blue-40 bold">
        Go back home
      </Link>

      <Suspense fallback={null}>
        <NotFoundTracker />
      </Suspense>
    </div>
  )
}
